<script>
import { mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import searchMixin from '@/mixins/searchMixin'
import { DisputePerspective } from '@/constants/disputes/DisputePerspective'
import SideDrawer from '@/components/general/side-drawer-dialog.vue'
import Spinner from '@/components/general/spinner.vue'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import { ShortTermStatus } from '@/constants/booking/ShortTermStatus'
import DataTableSortStrategy from '@/services/vuetify/dataTableSortStrategy'
import DisputesDataTableCellKeys from '@/constants/disputes/DisputesDataTableCellKeys'
import { DID_NOT_HAPPEN_DELTA_CODE } from '@/constants/disputes/DisputesDeltaCodes'
import DisputesDialogContent from './disputes-dialog/disputes-dialog-content.vue'
import InvoiceCenterDialogContent from './disputes-dialog/invoice-bulk-action-dialog-content.vue'
import DisputesTaskDialogContent from './disputes-dialog/disputes-dialog-task-form.vue'
import DisputesTableOverviewStatsBreakdownCard from './disputes-table-overview-stats-breakdown-card.vue'
import DisputeTableInvoiceCenterStatsBreakdownCard from './dispute-table-invoice-center-stats-breakdown-card.vue'
import DisputeResolutionConfirmationDialog from './dispute-resolution-confirmation-dialog.vue'
import DisputesTableV2 from './disputes-table-v2.vue'
import DisputesTaskTable from './disputes-task-table.vue'

const DISPUTES_TAB = 0
const TASKS_TAB = 1

export default {
  name: 'BookingDisputeListV2',
  components: {
    SideDrawer,
    DisputesDialogContent,
    InvoiceCenterDialogContent,
    DisputesTaskDialogContent,
    Spinner,
    DisputesTableV2,
    DisputesTaskTable,
    DisputesTableOverviewStatsBreakdownCard,
    DisputeTableInvoiceCenterStatsBreakdownCard,
    DisputeResolutionConfirmationDialog,
  },
  mixins: [baseMixin, searchMixin],
  data() {
    return {
      timesheetSelectTypes: ['All', 'Missing', 'Conflict'],
      filterModel: null,
      disputePerspective: DisputePerspective,
      isFlyoutLoading: false,
      isGraphLoading: false,
      invoiceCenterId: '',
      invoiceCenterDateUntil: null,
      graphLabels: ['Pending', 'Candidate', 'Client'],
      graphData: [],
      tableOverviewDataPoints: [],
      disputeCategoryFilterKeys: [],
      tab: DISPUTES_TAB,
      debouncedSearch: '',
      debouncedSearchHandler: null,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingBookingDisputes: 'disputes/isLoading',
      allowedToViewAdminIds: 'disputes/getAllowedToViewAdminIds',
      currentUser: 'auth/currentUser',
      activeTimesheet: 'disputes/activeTimesheet',
      resolveStatus: 'disputes/resolveStatus',
      showFlyout: 'disputes/showFlyout',
      showResolveContent: 'disputes/showResolveContent',
      showDisputesTaskContent: 'disputes/showDisputesTaskContent',
      showInvoiceCenterContent: 'disputes/showInvoiceCenterContent',
      getInvoiceCenterGroupedData: 'disputes/getInvoiceCenterGroupedData',
      isFinanceUser: 'auth/isFinanceUser',
      invoiceCenter: 'disputes/invoiceCenter',
      isLoadingDisputeSubmit: 'disputes/isLoadingDisputeSubmit',
      disputeTasks: 'disputes/disputeFollowUpTasks',
      getUserNameById: 'users/getUserNameById',
    }),
    autocompleteSelectedModel: {
      get() {
        return this.filterModel
      },
      set(val) {
        this.filterModel = val
      },
    },
    invoiceCenterData() {
      return this.invoiceCenter(this.invoiceCenterId)
    },
    isLoading() {
      return this.isFlyoutLoading || this.isLoadingDisputeSubmit
    },
    disputeBookingStatusFilter() {
      if (isNullOrEmptyArray(this.disputeCategoryFilterKeys)) return []

      const bookingStatuses = []

      for (const filterKey of this.disputeCategoryFilterKeys) {
        switch (filterKey) {
          case 'dispute_stats_not_submitted':
            bookingStatuses.push(ShortTermStatus.BOOKING_NOT_APPROVED)
            bookingStatuses.push(ShortTermStatus.ECH_BOOKING_NOT_APPROVED)
            break
          case 'dispute_stats_modified':
            bookingStatuses.push(
              ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED
            )
            bookingStatuses.push(
              ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
            )
            bookingStatuses.push(
              ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_MODIFIED
            )
            bookingStatuses.push(
              ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
            )
            bookingStatuses.push(ShortTermStatus.TIMESHEET_DISPUTE_OR_MISMATCH)
            break
          case 'dispute_stats_other':
            bookingStatuses.push(
              ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_NO_CHANGES
            )
            bookingStatuses.push(
              ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_NO_CHANGES
            )
            break
        }
      }

      return bookingStatuses
    },
    tabs() {
      return [
        {
          key: 'disputes',
          title: this.$t('dispute.tabs.disputes'),
          count: this.graphData?.length >= 100 ? '99+' : this.graphData?.length,
        },
        {
          key: 'tasks',
          title: this.$t('dispute.tabs.tasks'),
          count:
            this.disputeTasks?.length >= 100
              ? '99+'
              : this.disputeTasks?.length,
        },
      ]
    },
    sortConfig() {
      const {
        BOOKING_ID,
        CANDIDATE_NAME,
        LOCATION_NAME,
        BREAK_MINUTES,
        BOOKING_DATE_TIME,
        IS_REST_BREAK_COMPLIANT,
        CANDIDATE_HAS_SUBMITTED,
        CLIENT_HAS_SUBMITTED,
        DELTA_MINUTES,
        TASK_TYPE,
      } = DisputesDataTableCellKeys

      // Base configuration shared by both tables
      const baseConfig = {
        [BOOKING_ID]: {
          method: 'number',
          getValue: (item) => item.bookingId,
        },
        [CANDIDATE_NAME]: {
          method: 'string',
          getValue: (item) => item.candidateName,
        },
        [LOCATION_NAME]: {
          method: 'string',
          getValue: (item) => item.locationTitle,
        },
        [BREAK_MINUTES]: {
          method: 'number',
          getValue: (item) =>
            item?.candidateTimesheet?.breakMinutes ??
            item?.clientTimesheet?.breakMinutes ??
            item.breakMinutes,
        },
        [BOOKING_DATE_TIME]: {
          method: 'date',
          getValue: (item) => item.startTimeLocal,
        },
        [IS_REST_BREAK_COMPLIANT]: {
          method: 'booleanWeighted',
          getValue: (item) =>
            item?.candidateTimesheet?.isRestBreakCompliant ??
            item?.isRestBreakCompliant ??
            null,
        },
        [CANDIDATE_HAS_SUBMITTED]: {
          method: 'booleanWeighted',
          getValue: (item) =>
            item.hasCandidateTimesheet ?? item.candidateSubmittedDate,
        },
        [CLIENT_HAS_SUBMITTED]: {
          method: 'booleanWeighted',
          getValue: (item) =>
            item.hasClientTimesheet ?? item.clientSubmittedDate,
        },
      }

      if (this.tab === DISPUTES_TAB) {
        baseConfig[DELTA_MINUTES] = {
          method: 'number',
          getValue: (item) =>
            item.deltaMinutes !== DID_NOT_HAPPEN_DELTA_CODE
              ? item.deltaMinutes
              : 0,
        }
      }

      if (this.tab === TASKS_TAB) {
        baseConfig[TASK_TYPE] = {
          method: 'number',
          getValue: (item) => item.taskType,
        }
      }

      return baseConfig
    },
    sortStrategy() {
      return new DataTableSortStrategy(this.sortConfig)
    },
  },
  watch: {
    autocompleteSelectedModel: {
      async handler() {
        await this.loadTaskList()
      },
      deep: true,
    },

    tabs: {
      handler() {
        this.$refs.tabs.callSlider() // redraw tab underline on count change
      },
    },
    search: {
      handler(newValue) {
        if (this.debouncedSearchHandler === null) {
          this.debouncedSearchHandler = debounce((searchValue) => {
            this.debouncedSearch = searchValue
          }, 300)
        }
        this.debouncedSearchHandler(newValue)
      },
      immediate: true,
    },
  },
  async created() {
    if (!isNullOrEmptyArray(this.autocompleteSelectedModel?.ids))
      await this.loadTaskList()
  },
  methods: {
    ...mapActions({
      submitTimesheets: 'disputes/submitTimesheets',
      getDisputeDetailsById: 'disputes/getDisputeDetailsById',
      getDisputeFollowUpTasks: 'disputes/getDisputeFollowUpTasks',
    }),

    async loadTaskList() {
      this.$store.commit('disputes/START_LOADING_TASK_TABLE')
      await this.getDisputeFollowUpTasks({
        params: {
          filter: this.autocompleteSelectedModel,
        },
      })

      this.$store.commit('disputes/FINISH_LOADING_TASK_TABLE')
    },
    updateFilter(filterModel) {
      this.autocompleteSelectedModel = filterModel
    },
    setGraphData(data) {
      this.graphData = data
    },
    async loadList(refresh) {
      await this.$refs.disputesTable.loadList(refresh)
      await this.loadTaskList()
    },
    async loadBookingDetails(item) {
      this.isFlyoutLoading = true
      this.$store.commit('disputes/SET_FLYOUT', { key: 'show', value: true })

      const { data } = await this.getDisputeDetailsById(item.bookingId)

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'timesheet',
        value: data,
      })

      if (item?.taskId) await this.openTaskForm(item)

      this.isFlyoutLoading = false
    },
    async openTaskForm(item) {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'task',
        value: {
          bookingId: item.bookingId,
          id: item.taskId,
        },
      })
      await this.$store.dispatch('disputes/getTaskDetails')
    },
    openInvoiceCenterFlyout({ invoiceCenterId, date }) {
      this.isFlyoutLoading = true
      this.invoiceCenterId = invoiceCenterId
      this.invoiceCenterDateUntil = date

      this.$store.commit('disputes/SET_FLYOUT', { key: 'show', value: true })

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'invoiceCenter',
        value: this.invoiceCenterData,
      })

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'invoiceCenterDateUntil',
        value: this.invoiceCenterDateUntil,
      })

      this.isFlyoutLoading = false
    },
    closeFlyout() {
      this.$store.commit('disputes/RESET_FLYOUT')
    },
    async submitInvoiceCenterBulkAction() {
      this.isFlyoutLoading = true
      await this.bulkResolveByInvoiceCenter()
      this.isFlyoutLoading = false
      this.closeFlyout()
      await this.loadList(true)
    },
    handleFilterChange(payload) {
      this.disputeCategoryFilterKeys = payload
    },
  },
}
</script>

<template>
  <div class="mt-4">
    <v-row fill-height>
      <v-col cols="12" lg="6" fill-height>
        <DisputesTableOverviewStatsBreakdownCard
          :disputes="graphData"
          @filterUpdated="updateFilter"
          @filterChanged="handleFilterChange"
        />
      </v-col>
      <v-col v-if="isFinanceUser" cols="12" lg="6">
        <DisputeTableInvoiceCenterStatsBreakdownCard
          @openInvoiceCenterFlyout="openInvoiceCenterFlyout"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-tabs ref="tabs" v-model="tab" background-color="transparent">
          <v-tab v-for="{ title, key, count } in tabs" :key="key">
            {{ title }}
            {{ count ? `(${count})` : '' }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-text-field
          v-model="search"
          class="my-6 custom-elevation"
          solo
          clearable
          hide-details
          append-icon="mdi-magnify"
          label="Search"
        />
        <v-tabs-items
          v-model="tab"
          class="custom-elevation rounded-lg overflow-y-auto"
        >
          <v-tab-item>
            <DisputesTableV2
              ref="disputesTable"
              :autocomplete-selected-model="autocompleteSelectedModel"
              :status-filters="disputeBookingStatusFilter"
              :search="debouncedSearch"
              :sort-strategy="sortStrategy"
              @loadBookingDetails="loadBookingDetails"
              @setGraphData="setGraphData"
            />
          </v-tab-item>
          <v-tab-item>
            <DisputesTaskTable
              ref="taskTable"
              :autocomplete-selected-model="autocompleteSelectedModel"
              :search="debouncedSearch"
              :sort-strategy="sortStrategy"
              @openTask="loadBookingDetails"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
    <SideDrawer
      :show="showFlyout"
      :xl="5"
      :lg="6"
      :md="10"
      :title="$t('dispute.resolutionDialog.title')"
      @closeDialog="closeFlyout"
    >
      <DisputesDialogContent v-if="showResolveContent && !isLoading" />
      <InvoiceCenterDialogContent
        v-if="showInvoiceCenterContent && !isLoading"
      />
      <DisputesTaskDialogContent
        v-if="showDisputesTaskContent && !isLoading"
        @refreshTaskList="loadTaskList"
      />
      <Spinner v-if="isLoading" class="mt-10" size="60" />
      <template v-slot:actions="{ close }">
        <v-spacer />
        <v-btn
          data-testid="side-drawer-dialog-close-btn"
          :large="!isMediumOrLower"
          depressed
          :disabled="isLoading"
          :color="`grey ${isDarkMode ? 'darken-5' : 'lighten-4'}`"
          class="px-8"
          @click="close"
        >
          {{
            showDisputesTaskContent
              ? $t('dispute.task.sideDrawerClose')
              : $t('general.cancel')
          }}
        </v-btn>
      </template>
    </SideDrawer>
    <DisputeResolutionConfirmationDialog @complete="loadList(true)" />
  </div>
</template>

<style>
.data-table-empty-cell-offset {
  margin-left: -10px;
}

.data-table-icon-cell-offset {
  margin-left: -5px;
}
</style>
